import apiRequest from "./apiClient";
import jwt_decode from "jwt-decode";

class AuthService {
    async Login(params) {
        try {
            const response = await apiRequest("/auth/authenticate", {
                method: "POST",
                credentials: "include",
                data: params
            });
            
            localStorage.setItem("x-auth-token", JSON.stringify(response.data));
            return response;
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    }

    async LoginAzureAd(params) {
        try {
            const response = await apiRequest("/auth/ad", {
                method: "POST",
                credentials: "include",
                data: params
            });
            
            localStorage.setItem("x-auth-token", JSON.stringify(response.data));
            
        // Limpiar cache de foto anterior si existe
        this.clearUserPhotoCache();
        
        // Guardar el token original de Azure AD si está disponible
        if (params.azureAccessToken) {
            localStorage.setItem("azure-access-token", params.azureAccessToken);
        }
            
            return response;
        } catch (error) {
            console.error('Azure AD Login error:', error);
            throw error;
        }
    }
    
    async RefreshToken(token) {
        try {
            const { accessToken, refreshToken } = typeof(token) === 'string' ? JSON.parse(token) : token;
            const response = await apiRequest("/auth/refreshToken", {
                method: "POST",
                data: { accessToken, refreshToken }
            });
            
            localStorage.setItem("x-auth-token", JSON.stringify(response.data));
            return response;
        } catch (error) {
            console.error('Token refresh error:', error);
            throw error;
        }
    }

    async GetAllowedCompanies() {
        try {
            const response = await apiRequest("/company/allowed?$select=id,name,commercialName,identity", {
                method: "GET"
            });
            return response;
        } catch (error) {
            console.error('Get allowed companies error:', error);
            throw error;
        }
    }

    async ChangeCompany(access) {
        try {
            const response = await apiRequest(`/account/changeCompany?companyId=${access.company.id}&accessUserId=${access.id}`, {
                method: "POST"
            });
            
            localStorage.setItem("x-auth-token", JSON.stringify(response.data));
            
            // Obtener las empresas permitidas después del cambio de empresa (OBLIGATORIO)
            try {
                const companiesResponse = await this.GetAllowedCompanies();
                localStorage.setItem("allowedCompanies", JSON.stringify(companiesResponse.data));
            } catch (error) {
                console.error('ChangeCompany: CRITICAL ERROR fetching allowed companies:', error);
                throw new Error(`Error obteniendo empresas permitidas después del cambio: ${error.message}`);
            }
            
            return response;
        } catch (error) {
            console.error('Change company error:', error);
            throw error;
        }
    }
    
    async Logout() {
        try {
            // Intentar hacer logout en el servidor
            await apiRequest("/auth/logout", {
                method: "POST",
                timeout: 5000 // 5 segundos de timeout
            });
        } catch (error) {
            console.error('Logout error:', error);
            // No lanzamos el error, solo lo registramos
        } finally {
            // Siempre limpiamos el localStorage completamente
            localStorage.removeItem("x-auth-token");
            localStorage.removeItem("userInfo");
            localStorage.removeItem("allowedCompanies"); // Limpiar empresas permitidas
            localStorage.removeItem("azure-access-token"); // Limpiar token de Azure AD
            
            // Limpiar cache de foto del usuario
            this.clearUserPhotoCache();
        }
    }

    CurrentUser() {
        const token = localStorage.getItem('x-auth-token');
        if (token) {
            try {
                return jwt_decode(JSON.parse(token).accessToken);
            } catch (error) {
                console.error('Token decode error:', error);
                localStorage.removeItem("x-auth-token");
            }
        }
        return null;
    }

    // Método para verificar si el token está próximo a expirar
    isTokenExpiringSoon(minutesThreshold = 5) {
        const token = localStorage.getItem('x-auth-token');
        if (!token) return true;
        
        try {
            const decoded = jwt_decode(JSON.parse(token).accessToken);
            const now = Date.now() / 1000;
            const timeUntilExpiry = decoded.exp - now;
            return timeUntilExpiry < (minutesThreshold * 60);
        } catch (error) {
            return true;
        }
    }

    // Cache para la foto del usuario durante la sesión
    userPhotoCache = null;
    userPhotoTokenHash = null; // Hash del token para validar que es el mismo usuario

    // Método para obtener la foto del usuario desde Azure AD
    async getUserPhoto() {
        try {
            // Obtener el token original de Azure AD
            const azureAccessToken = localStorage.getItem('azure-access-token');
            if (!azureAccessToken) {
                console.log('No se encontró el token de Azure AD');
                return null;
            }

            // Crear hash del token para validar que es el mismo usuario
            const tokenHash = btoa(azureAccessToken.substring(0, 20)); // Hash simple del token
            
            // Verificar si ya tenemos la foto en cache (memoria) y es del mismo usuario
            if (this.userPhotoCache && this.userPhotoTokenHash === tokenHash) {
                console.log('Usando foto del usuario desde cache en memoria');
                // Verificar que el blob URL sigue siendo válido
                try {
                    await fetch(this.userPhotoCache, { method: 'HEAD' });
                    return this.userPhotoCache;
                } catch (error) {
                    console.log('Blob URL inválido, limpiando cache');
                    this.clearUserPhotoCache();
                }
            }

            // Verificar si tenemos la foto en localStorage (persistente) y es del mismo usuario
            const cachedPhotoUrl = localStorage.getItem('user-photo-cache');
            const cachedTokenHash = localStorage.getItem('user-photo-token-hash');
            
            if (cachedPhotoUrl && cachedTokenHash === tokenHash) {
                console.log('Usando foto del usuario desde cache persistente');
                // Verificar que el blob URL sigue siendo válido
                try {
                    await fetch(cachedPhotoUrl, { method: 'HEAD' });
                    this.userPhotoCache = cachedPhotoUrl; // Restaurar cache en memoria
                    this.userPhotoTokenHash = tokenHash; // Restaurar hash del token
                    return cachedPhotoUrl;
                } catch (error) {
                    console.log('Blob URL persistente inválido, limpiando cache');
                    this.clearUserPhotoCache();
                }
            }
            
            console.log('Obteniendo foto del usuario desde Microsoft Graph API');
            // Hacer la petición a Microsoft Graph API para obtener la foto
            const response = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
                headers: {
                    'Authorization': `Bearer ${azureAccessToken}`,
                    'Content-Type': 'image/jpeg'
                }
            });
            
            if (response.ok) {
                const blob = await response.blob();
                const photoUrl = URL.createObjectURL(blob);
                
                // Guardar en cache en memoria
                this.userPhotoCache = photoUrl;
                this.userPhotoTokenHash = tokenHash;
                
                // Guardar en localStorage para persistencia
                localStorage.setItem('user-photo-cache', photoUrl);
                localStorage.setItem('user-photo-token-hash', tokenHash);
                
                console.log('Foto del usuario guardada en cache (memoria y persistente)');
                
                return photoUrl;
            } else {
                console.log('No se pudo obtener la foto del usuario:', response.status);
                return null;
            }
        } catch (error) {
            console.error('Error obteniendo foto del usuario:', error);
            return null;
        }
    }

    // Método para limpiar el cache de la foto del usuario
    clearUserPhotoCache() {
        // Limpiar cache en memoria
        if (this.userPhotoCache) {
            URL.revokeObjectURL(this.userPhotoCache);
            this.userPhotoCache = null;
        }
        this.userPhotoTokenHash = null;
        
        // Limpiar cache persistente
        const cachedPhotoUrl = localStorage.getItem('user-photo-cache');
        if (cachedPhotoUrl) {
            URL.revokeObjectURL(cachedPhotoUrl);
            localStorage.removeItem('user-photo-cache');
        }
        localStorage.removeItem('user-photo-token-hash');
        
        console.log('Cache de foto del usuario limpiado (memoria y persistente)');
    }
}

export default new AuthService();
